// How PintaFlow works — quick, scannable user guide.
import { useNavigate } from "react-router-dom";
import { Lightbulb, Kanban, Wand2, BarChart3, Target, Bookmark, Calendar, ArrowRight, PlayCircle } from "lucide-react";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";

// Order + numerals MATCH the sidebar navigation so users have one consistent
// mental model. Sidebar: i.Home → ii.Pipeline → iii.Vault → iv.Scripts →
// v.Swipe → vi.Calendar → vii.Analytics → viii.Goals → ix.How it works.
// Home / How it works / Pricing / Settings are not feature explainers,
// so they're omitted here. Each card carries the same Roman numeral as
// its sidebar counterpart.
const SECTIONS = [
  {
    n: "ii",
    title: "The",
    titleItalic: "Pipeline",
    icon: Kanban,
    body: "Six rooms, one craft. Drag (or tap →) any idea through Idea → Research → Script → Create → Schedule → Published. Visual progress, zero spreadsheets.",
    cta: "Open Pipeline",
    route: "/pipeline",
  },
  {
    n: "iii",
    title: "The",
    titleItalic: "Vault",
    icon: Lightbulb,
    body: "Every spark — late-night thought, half-baked idea, random observation — lands here first. Tag it, attach a platform, and it never gets lost. Promote ideas into the Pipeline when you're ready.",
    cta: "Open Vault",
    route: "/vault",
  },
  {
    n: "iv",
    title: "AI",
    titleItalic: "Co-Pilot",
    icon: Wand2,
    body: "Tell the Co-Pilot your topic, platform, tone. Get Hook + Intro + Body + CTA in under 10 seconds. Powered by GPT-5.2. Every script saves to your Library automatically.",
    cta: "Write a script",
    route: "/scripts",
  },
  {
    n: "v",
    title: "Swipe",
    titleItalic: "File",
    icon: Bookmark,
    body: "Save viral hooks, thumbnails, transitions — anything you find. Build your own inspiration library that compounds over time. Top creators don't memorize, they archive.",
    cta: "Open Swipe File",
    route: "/swipe",
  },
  {
    n: "vi",
    title: "The",
    titleItalic: "Calendar",
    icon: Calendar,
    body: "Drop scheduled ideas onto a quiet monthly grid. See your publishing rhythm at a glance — gaps, clusters, sprints. Drag any idea card to reschedule in one tap.",
    cta: "Open Calendar",
    route: "/calendar",
  },
  {
    n: "vii",
    title: "Quiet",
    titleItalic: "Analytics",
    icon: BarChart3,
    body: "Log your numbers — views, likes, comments, revenue. PintaFlow shows the mirror without anxiety. See which platform earns and where to double down.",
    cta: "Open Analytics",
    route: "/analytics",
  },
  {
    n: "viii",
    title: "Goals &",
    titleItalic: "Streak",
    icon: Target,
    body: "Set one quiet weekly or monthly target. PintaFlow auto-tracks every publish against it. Heatmap on the dashboard shows your last 84 days like GitHub — consistency made visible.",
    cta: "Set a goal",
    route: "/goals",
  },
];

export default function HowItWorks() {
  const navigate = useNavigate();
  const replay = () => {
    window.dispatchEvent(new CustomEvent("pintaflow:coach-replay"));
    navigate("/dashboard");
  };
  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-12 max-w-3xl">
        <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> v. How it works</div>
        <h1 className="font-heading mt-4 text-4xl md:text-6xl leading-[1.05]" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
          Idea to publish, <span className="font-display-italic">one calm room</span>.
        </h1>
        <p className="text-base mt-5" style={{ color: INK_72 }}>
          PintaFlow replaces 7 creator tools (Notion + Trello + Docs + Calendar + ChatGPT + Analytics dashboards + Swipe folder) with a single focused workspace. Here&apos;s how each piece fits.
        </p>
        <button onClick={replay} className="btn-secondary mt-6" data-testid="howitworks-replay-coach">
          <PlayCircle size={14} /> Replay the 5-step coach
        </button>
      </div>

      <div className="space-y-4">
        {SECTIONS.map(({ n, title, titleItalic, icon: Icon, body, cta, route }) => (
          <article
            key={n}
            className="card grid grid-cols-[auto,1fr] gap-5 md:gap-8 items-start"
            data-testid={`howitworks-section-${n}`}
          >
            <div className="flex flex-col items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: SEAL }}>{n}.</span>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: "rgba(216,168,87,0.10)", border: "1px solid rgba(216,168,87,0.30)" }}
              >
                <Icon size={20} style={{ color: SEAL }} strokeWidth={1.4} />
              </div>
            </div>
            <div className="min-w-0">
              <h2 className="font-heading text-2xl md:text-3xl leading-tight" style={{ fontVariationSettings: "'opsz' 64, 'wght' 450" }}>
                {title} <span className="font-display-italic">{titleItalic}</span>
              </h2>
              <p className="text-sm md:text-base mt-3 leading-relaxed" style={{ color: INK_72 }}>{body}</p>
              <button
                onClick={() => navigate(route)}
                className="btn-ghost mt-4 text-xs"
                style={{ color: SEAL }}
                data-testid={`howitworks-cta-${n}`}
              >
                {cta} <ArrowRight size={12} />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
